import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeftIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  UserIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  DocumentArrowDownIcon,
  PencilIcon,
  TrashIcon
} from '@heroicons/react/24/outline'
import { useAuthStore } from '@/stores/authStore'
import { useToast } from '@/hooks/useToast'
import ToastContainer from '@/components/ui/ToastContainer'
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts'

interface ContaReceber {
  id: number
  descricao: string
  valor: number
  data_vencimento: string
  data_pagamento?: string
  status: string
  categoria?: { id: number; nome: string }
  cliente?: { id: number; nome: string }
  observacoes?: string
  recorrente?: boolean
  created_at: string
  updated_at?: string
}

interface Historico {
  id: number
  data: string
  acao: string
  usuario?: string
  detalhes?: string
}

const ContasReceberDetalhes: React.FC = () => {
  const { contaId } = useParams<{ contaId: string }>()
  const navigate = useNavigate()
  const { token } = useAuthStore()
  const { toasts, success, error, removeToast } = useToast()

  const [conta, setConta] = useState<ContaReceber | null>(null)
  const [historico, setHistorico] = useState<Historico[]>([])
  const [loading, setLoading] = useState(true)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showReceiveModal, setShowReceiveModal] = useState(false)

  const [formData, setFormData] = useState({
    descricao: '',
    valor: '',
    data_vencimento: '',
    observacoes: ''
  })

  const [receiveData, setReceiveData] = useState({
    data_pagamento: new Date().toISOString().split('T')[0],
    observacoes: ''
  })

  useEffect(() => {
    if (contaId) {
      fetchContaDetails()
    }
  }, [contaId])

  const fetchContaDetails = async () => {
    try {
      setLoading(true)

      const response = await fetch(`${API_BASE_URL}/financeiro/contas-receber/${contaId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        error('Conta não encontrada', 'A conta solicitada não existe')
        navigate('/financeiro/contas-receber')
        return
      }

      const data: ContaReceber = await response.json()
      setConta(data)

      // Simular histórico (pode ser expandido para vir do backend)
      const mockHistorico: Historico[] = [
        {
          id: 1,
          data: data.created_at,
          acao: 'Conta criada',
          usuario: 'Sistema',
          detalhes: 'Conta a receber cadastrada no sistema'
        }
      ]

      if (data.data_pagamento) {
        mockHistorico.push({
          id: 2,
          data: data.data_pagamento,
          acao: 'Recebimento confirmado',
          usuario: 'Financeiro',
          detalhes: `Valor de ${formatCurrency(parseFloat(data.valor.toString()))} recebido`
        })
      }

      setHistorico(mockHistorico)
    } catch (err: any) {
      error('Erro ao carregar dados', err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = () => {
    if (!conta) return

    setFormData({
      descricao: conta.descricao,
      valor: conta.valor.toString(),
      data_vencimento: conta.data_vencimento,
      observacoes: conta.observacoes || ''
    })
    setShowEditModal(true)
  }

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!conta) return

    try {
      const response = await fetch(`${API_BASE_URL}/financeiro/contas-receber/${conta.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          descricao: formData.descricao,
          valor: parseFloat(formData.valor),
          data_vencimento: formData.data_vencimento,
          observacoes: formData.observacoes
        })
      })

      if (!response.ok) {
        throw new Error('Erro ao atualizar conta')
      }

      success('Sucesso!', 'Conta atualizada com sucesso')
      setShowEditModal(false)
      fetchContaDetails()
    } catch (err: any) {
      error('Erro', err.message)
    }
  }

  const handleReceive = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!conta) return

    try {
      const response = await fetch(`${API_BASE_URL}/financeiro/contas-receber/${conta.id}/marcar-recebido`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error('Erro ao processar recebimento')
      }

      success('Recebimento confirmado!', 'Conta marcada como recebida')
      setShowReceiveModal(false)
      fetchContaDetails()
    } catch (err: any) {
      error('Erro', err.message)
    }
  }

  const handleDelete = async () => {
    if (!conta || !window.confirm('Tem certeza que deseja excluir esta conta a receber?')) return

    try {
      const response = await fetch(`${API_BASE_URL}/financeiro/contas-receber/${conta.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error('Erro ao excluir conta')
      }

      success('Sucesso!', 'Conta excluída com sucesso')
      navigate('/financeiro/contas-receber')
    } catch (err: any) {
      error('Erro', err.message)
    }
  }

  const exportToCSV = () => {
    if (!conta) return

    const csvContent = [
      ['Campo', 'Valor'],
      ['ID', conta.id],
      ['Descrição', conta.descricao],
      ['Valor', formatCurrency(parseFloat(conta.valor.toString()))],
      ['Data Vencimento', formatDate(conta.data_vencimento)],
      ['Data Pagamento', conta.data_pagamento ? formatDate(conta.data_pagamento) : 'Não pago'],
      ['Status', getStatusLabel(conta.status)],
      ['Cliente', conta.cliente?.nome || 'N/A'],
      ['Categoria', conta.categoria?.nome || 'N/A'],
      ['Observações', conta.observacoes || 'N/A']
    ].map(row => row.join(';')).join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `conta_receber_${conta.id}_${new Date().toISOString().split('T')[0]}.csv`
    link.click()

    success('Exportado!', 'Arquivo CSV gerado com sucesso')
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR')
  }

  const getStatusColor = (status: string) => {
    switch (status.toUpperCase()) {
      case 'PAGO':
      case 'PAID':
        return 'bg-green-100 text-green-800'
      case 'PENDENTE':
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800'
      case 'VENCIDO':
      case 'OVERDUE':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status.toUpperCase()) {
      case 'PAGO':
      case 'PAID':
        return 'Recebido'
      case 'PENDENTE':
      case 'PENDING':
        return 'Pendente'
      case 'VENCIDO':
      case 'OVERDUE':
        return 'Vencido'
      default:
        return status
    }
  }

  const getDaysUntilDue = () => {
    if (!conta) return 0
    const today = new Date()
    const due = new Date(conta.data_vencimento)
    const diffTime = due.getTime() - today.getTime()
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    )
  }

  if (!conta) {
    return null
  }

  const daysUntilDue = getDaysUntilDue()

  return (
    <div className="space-y-6 pb-6">
      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/financeiro/contas-receber')}
              className="text-gray-600 hover:text-gray-900 p-2 rounded-lg hover:bg-gray-100"
            >
              <ArrowLeftIcon className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Detalhes da Conta</h1>
              <p className="text-gray-600">{conta.descricao}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={exportToCSV}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2 transition-colors"
            >
              <DocumentArrowDownIcon className="w-5 h-5" />
              Exportar CSV
            </button>
            {(conta.status === 'PENDENTE' || conta.status === 'pending') && (
              <button
                onClick={() => setShowReceiveModal(true)}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
              >
                Marcar como Recebido
              </button>
            )}
          </div>
        </div>

        {/* Status Badge */}
        <div className="flex items-center gap-4">
          <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(conta.status)}`}>
            {getStatusLabel(conta.status)}
          </span>
          {conta.recorrente && (
            <span className="inline-flex px-3 py-1 text-sm font-semibold rounded-full bg-purple-100 text-purple-800">
              Recorrente
            </span>
          )}
        </div>
      </div>

      {/* Main Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm">Valor Total</p>
              <p className="text-3xl font-bold mt-1">{formatCurrency(parseFloat(conta.valor.toString()))}</p>
            </div>
            <CurrencyDollarIcon className="w-10 h-10 text-green-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm">Data de Vencimento</p>
              <p className="text-xl font-bold mt-1">{formatDate(conta.data_vencimento)}</p>
              {(conta.status === 'PENDENTE' || conta.status === 'pending') && (
                <p className="text-sm mt-1">
                  {daysUntilDue < 0 ? `${Math.abs(daysUntilDue)} dias atrasado` :
                   daysUntilDue === 0 ? 'Vence hoje' :
                   `Faltam ${daysUntilDue} dias`}
                </p>
              )}
            </div>
            <CalendarIcon className="w-10 h-10 text-blue-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm">Cliente</p>
              <p className="text-xl font-bold mt-1">{conta.cliente?.nome || 'Não informado'}</p>
              {conta.categoria && (
                <p className="text-sm mt-1">{conta.categoria.nome}</p>
              )}
            </div>
            <UserIcon className="w-10 h-10 text-purple-200" />
          </div>
        </div>
      </div>

      {/* Details Section */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Informações Detalhadas</h3>
          <div className="flex gap-2">
            <button
              onClick={handleEdit}
              className="text-blue-600 hover:text-blue-900 flex items-center gap-1 text-sm"
            >
              <PencilIcon className="w-4 h-4" />
              Editar
            </button>
            <button
              onClick={handleDelete}
              className="text-red-600 hover:text-red-900 flex items-center gap-1 text-sm"
            >
              <TrashIcon className="w-4 h-4" />
              Excluir
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <p className="text-sm text-gray-600">ID da Conta</p>
            <p className="font-medium text-gray-900">#{conta.id}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Data de Criação</p>
            <p className="font-medium text-gray-900">{formatDate(conta.created_at)}</p>
          </div>
          {conta.data_pagamento && (
            <div>
              <p className="text-sm text-gray-600">Data de Pagamento</p>
              <p className="font-medium text-gray-900">{formatDate(conta.data_pagamento)}</p>
            </div>
          )}
          {conta.updated_at && (
            <div>
              <p className="text-sm text-gray-600">Última Atualização</p>
              <p className="font-medium text-gray-900">{formatDate(conta.updated_at)}</p>
            </div>
          )}
        </div>

        {conta.observacoes && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-600 mb-2">Observações</p>
            <p className="text-gray-900">{conta.observacoes}</p>
          </div>
        )}
      </div>

      {/* Histórico */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Histórico de Movimentações</h3>
        <div className="space-y-4">
          {historico.map((item) => (
            <div key={item.id} className="flex items-start gap-4 pb-4 border-b border-gray-100 last:border-0">
              <div className="bg-green-100 rounded-full p-2">
                <DocumentTextIcon className="w-5 h-5 text-green-600" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <p className="font-medium text-gray-900">{item.acao}</p>
                  <p className="text-sm text-gray-500">{formatDate(item.data)}</p>
                </div>
                {item.detalhes && (
                  <p className="text-sm text-gray-600 mt-1">{item.detalhes}</p>
                )}
                {item.usuario && (
                  <p className="text-xs text-gray-500 mt-1">Por: {item.usuario}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg">
            <h3 className="text-lg font-semibold mb-4">Editar Conta a Receber</h3>
            <form className="space-y-4" onSubmit={handleUpdate}>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição
                </label>
                <input
                  type="text"
                  value={formData.descricao}
                  onChange={(e) => setFormData({...formData, descricao: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Valor
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.valor}
                    onChange={(e) => setFormData({...formData, valor: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data Vencimento
                  </label>
                  <input
                    type="date"
                    value={formData.data_vencimento}
                    onChange={(e) => setFormData({...formData, data_vencimento: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Observações
                </label>
                <textarea
                  value={formData.observacoes}
                  onChange={(e) => setFormData({...formData, observacoes: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  rows={3}
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                >
                  Salvar Alterações
                </button>
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="flex-1 bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Receive Modal */}
      {showReceiveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Confirmar Recebimento</h3>
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">Conta:</p>
              <p className="font-medium">{conta.descricao}</p>
              <p className="text-lg font-bold text-green-600">{formatCurrency(parseFloat(conta.valor.toString()))}</p>
            </div>

            <form className="space-y-4" onSubmit={handleReceive}>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data do Recebimento
                </label>
                <input
                  type="date"
                  value={receiveData.data_pagamento}
                  onChange={(e) => setReceiveData({...receiveData, data_pagamento: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Observações
                </label>
                <textarea
                  value={receiveData.observacoes}
                  onChange={(e) => setReceiveData({...receiveData, observacoes: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  rows={3}
                  placeholder="Observações sobre o recebimento..."
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
                >
                  Confirmar Recebimento
                </button>
                <button
                  type="button"
                  onClick={() => setShowReceiveModal(false)}
                  className="flex-1 bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ToastContainer toasts={toasts} onClose={removeToast} />
    </div>
  )
}

export default ContasReceberDetalhes
